import {
  Field,
  Item,
  Text,
  RichText,
  getFieldValue,
  useSitecoreContext,
  LayoutServicePageState,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';
import type { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import type { RecordOfFields } from 'models/fields';
import Location from './Location';
import { LocationProps } from './Location.Types';
import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';
import mockData from './mock.data.location.json';

import styles from './LocationsListing.module.scss';
import { useRouter } from 'next/router';
import { useI18n } from 'next-localization';
import Loader from 'components/helpers/Loader/Loader';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';

type LocationsListingProps = ComponentProps & {
  fields: {
    heading: Field<string>;
    headingLevel: Item | null;
    headingTextStyle: Item | null;
    description: Field<string>;
    displayMode: Item | null;
    locations: LocationProps[];
  };
};

const cx = classNames.bind(styles);

const LocationsListing = (props: LocationsListingProps): React.JSX.Element => {
  const { fields } = props;
  const router = useRouter();
  const headingLevelItemField = fields.headingLevel?.fields as RecordOfFields;
  const headingLevelValue = getFieldValue<string>(headingLevelItemField, 'Value') || 'h1';
  const { t } = useI18n();

  const { sitecoreContext } = useSitecoreContext();

  const headingTextStyleItemField = fields.headingTextStyle?.fields as RecordOfFields;
  const headingTextStyleValue = getFieldValue<string>(headingTextStyleItemField, 'Value') || 'font-style-xl';

  const displayModeItemField = fields.displayMode?.fields as RecordOfFields;
  const displayModeValue = getFieldValue<string>(displayModeItemField, 'Value') || 'singleColumn';
  const [locations, setLocations] = useState<LocationProps[]>([]);
  const useMockData = sitecoreContext.pageState !== LayoutServicePageState.Normal;
  useEffect(() => {
    if (useMockData) {
      const tempData = JSON.parse(JSON.stringify(mockData)) as LocationProps[];
      setLocations(tempData);
    } else {
      setLocations(fields.locations);
    }
  }, []);

  if (useMockData) {
    return (
      <>
        <Text
          className={cx({ [headingTextStyleValue]: true })}
          field={fields.heading}
          tag={headingLevelValue}
          editable={true}
          encode={sitecoreContext.pageEditing}
        />
        <RichText className={cx('body')} field={fields.description} tag="div" />
        <div role="status">
          {locations.length > 0 ? (
            <span className={cx('screen-reader-only')}>{t('Locations loaded')}</span>
          ) : (
            <Loader />
          )}
        </div>
        <div className={cx({ ['locations']: true, [displayModeValue]: true })} hidden={locations.length === 0}>
          {locations.map((loc, i) => (
            <Location key={i + loc.id} id={i.toString() + loc.id} fields={loc.fields} />
          ))}
        </div>
      </>
    );
  }
  return (
    <>
      <Text
        className={cx({ [headingTextStyleValue]: true })}
        field={fields.heading}
        tag={headingLevelValue}
        editable={true}
        encode={sitecoreContext.pageEditing}
      />
      <RichTextWrapper className={cx('body')} field={fields.description} tag="div" />
      <div role="status">
        {router.isReady ? <span className={cx('screen-reader-only')}>{t('Locations loaded')}</span> : <Loader />}
      </div>
      <div className={cx({ ['locations']: true, [displayModeValue]: true })} hidden={!router.isReady}>
        {locations.map((loc, i) => (
          <Location key={i + loc.id} id={i.toString() + loc.id} fields={loc.fields} />
        ))}
      </div>
    </>
  );
};

export default compose<LocationsListingProps>(withValidDatasourceCheck(), withStyles())(LocationsListing);
